import { CustomField, ItemType } from '../../../@types/python.d';
import { CustomFieldCollection } from '../../components/UserFields/types.d';

export type CustomFieldType =
	| 'reservations'
	| 'check-outs'
	| 'items'
	| 'kits'
	| 'users'
	| 'tickets'
	| CustomFieldCollection;

/** The keys the group payload carries field definitions under. */
export type GroupFieldKey =
	| 'reservationFields'
	| 'orderFields'
	| 'itemFields'
	| 'kitFields'
	| 'customerFields'
	| 'ticketFields'
	| 'locationFields';

export interface GroupCustomFieldsResponse {
	reservationFields: CustomField[];
	orderFields: CustomField[];
	itemFields: CustomField[];
	kitFields: CustomField[];
	customerFields: CustomField[];
	ticketFields?: CustomField[];
	locationFields?: CustomField[];
	itemTypes: ItemType[];
}

/** A group payload that may only carry the one key a caller asked for. */
type PartialGroupFields = Partial<Record<GroupFieldKey, CustomField[]>>;

interface CollectionSpec {
	/** Where this collection's definitions live on the group payload. */
	fieldKey: GroupFieldKey;
	/** Every type name that reads the same definitions, so cache invalidation hits them all. */
	aliases: CustomFieldType[];
}

/**
 * One table per collection. The fetched `_fields` list, the select, the initial-data seed
 * and the cache invalidation all derive from it, so a new collection is one entry here
 * rather than four edits that can silently disagree.
 */
const COLLECTIONS = {
	items: { fieldKey: 'itemFields', aliases: ['items'] },
	kits: { fieldKey: 'kitFields', aliases: ['kits'] },
	users: { fieldKey: 'customerFields', aliases: ['users', 'customers'] },
	customers: { fieldKey: 'customerFields', aliases: ['users', 'customers'] },
	reservations: { fieldKey: 'reservationFields', aliases: ['reservations'] },
	orders: { fieldKey: 'orderFields', aliases: ['check-outs', 'orders'] },
	'check-outs': { fieldKey: 'orderFields', aliases: ['check-outs', 'orders'] },
	tickets: { fieldKey: 'ticketFields', aliases: ['tickets'] },
	locations: { fieldKey: 'locationFields', aliases: ['locations'] },
} as const satisfies Record<CustomFieldType, CollectionSpec>;

const specFor = (type: CustomFieldType): CollectionSpec | undefined => COLLECTIONS[type as keyof typeof COLLECTIONS];

/**
 * The `_fields` the group query asks for. Derived from the table, so a collection can
 * never be selectable without also being requested.
 */
export const GROUP_CUSTOM_FIELDS_PARAM = [
	...new Set(Object.values(COLLECTIONS).map((spec) => spec.fieldKey)),
	'itemTypes',
].join(',');

/**
 * One collection's definitions out of a group payload.
 *
 * Always an array: `ticketFields` and `locationFields` are optional on the response, and
 * a workspace whose API predates a collection returns neither the key nor an empty list.
 */
export const selectCustomFields = (data: PartialGroupFields, type: CustomFieldType): CustomField[] => {
	const spec = specFor(type);

	return spec ? (data[spec.fieldKey] ?? []) : [];
};

/**
 * The query's initial data, read from the group the legacy shell already holds, so the
 * fields render before the request resolves. Only the requested collection's key is
 * seeded - seeding the rest would publish stale definitions for collections this caller
 * never asked about.
 */
export const seedCustomFieldsFromGroup = (type: CustomFieldType, group: PartialGroupFields): PartialGroupFields => {
	const spec = specFor(type);

	return spec ? { [spec.fieldKey]: group[spec.fieldKey] } : {};
};

/**
 * Every type name whose cache holds these definitions. `orders` and `check-outs` are the
 * same data under two keys, as are `users` and `customers`, so invalidating one alone
 * leaves the other serving what was just changed.
 */
export const invalidationTypesFor = (type: CustomFieldType): CustomFieldType[] | undefined => specFor(type)?.aliases;
